/**
 * Per-agent (per-certificate) drift detail page — P1b of the drift plan.
 *
 * Shows everything the mesh drawer's Drift section can't: full
 * fingerprint history as a vertical timeline, baseline transitions
 * across approvals, the certificate effective status, and an approve
 * action for suspended certs (P1c).
 *
 * Route: /certificates/:certId
 * Linked from CertificatesPage rows + mesh drawer when Drift lens is
 * active (P1c).
 */
import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft'
import { analyticsApi } from '../services/api'

const VERDICT_COLOR: Record<string, string> = {
  STABLE: '#9e9e9e',
  COSMETIC_DRIFT: '#ffc107',
  MATERIAL_DRIFT: '#fb8c00',
  CRITICAL_DRIFT: '#d32f2f',
  POSITIVE_DRIFT: '#388e3c',
  REBASELINE: '#1976d2',
  APPROVED: '#7b1fa2',
  RENEWED: '#7b1fa2',
}

const STATUS_COLOR: Record<string, 'success' | 'warning' | 'error' | 'default' | 'info'> = {
  active: 'success',
  suspended: 'warning',
  expired: 'error',
  revoked: 'error',
}

export default function AgentDriftDetailPage() {
  const { certId } = useParams<{ certId: string }>()
  const navigate = useNavigate()
  const [cert, setCert] = useState<any>(null)
  const [approvals, setApprovals] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [approveOpen, setApproveOpen] = useState(false)
  const [approveReason, setApproveReason] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [reloadKey, setReloadKey] = useState(0)

  useEffect(() => {
    if (!certId) return
    let cancelled = false
    setLoading(true)
    setError(null)
    Promise.all([
      analyticsApi.getCertificate(certId),
      analyticsApi.listDriftApprovals(certId).catch(() => ({ data: { approvals: [] } })),
    ])
      .then(([certResp, apprResp]) => {
        if (cancelled) return
        setCert(certResp.data)
        setApprovals(apprResp.data?.approvals || [])
      })
      .catch((e) => {
        if (cancelled) return
        setError(e?.response?.data?.detail || e?.message || 'Failed to load certificate')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [certId, reloadKey])

  const onApprove = async () => {
    if (!certId || !approveReason.trim()) return
    setSubmitting(true)
    setError(null)
    try {
      await analyticsApi.approveDrift(certId, { reason: approveReason.trim() })
      setApproveOpen(false)
      setApproveReason('')
      setReloadKey((k) => k + 1)
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Approval failed')
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    )
  }
  if (error && !cert) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    )
  }
  if (!cert) return null

  const effectiveStatus = (cert.status || 'active').toLowerCase()
  const canApprove = effectiveStatus === 'suspended'
  const driftHistory = cert.drift_history || []
  const driftEvents = cert.drift_events || []
  const driftStats = cert.drift_stats || {}

  // Build a unified timeline from drift_history + approvals, sorted DESC.
  const timeline = [
    ...driftHistory.map((h: any) => ({
      kind: h.verdict === 'APPROVED' || h.verdict === 'RENEWED' ? 'action' : 'drift',
      timestamp: h.timestamp,
      verdict: h.verdict,
      improvement: h.improvement,
      drift_score: h.drift_score,
      approved_by: h.approved_by,
      approved_verdict: h.approved_verdict,
    })),
    ...approvals.map((a: any) => ({
      kind: 'approval',
      timestamp: a.created_at,
      approved_by_email: a.approved_by_email,
      approved_by_user_id: a.approved_by_user_id,
      approved_verdict: a.approved_verdict,
      reason: a.reason,
    })),
  ].sort((a, b) => (b.timestamp || '').localeCompare(a.timestamp || ''))

  return (
    <Box sx={{ p: 3, maxWidth: 1100, mx: 'auto' }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <IconButton size="small" onClick={() => navigate('/certificates')}>
          <KeyboardArrowLeft />
        </IconButton>
        <Typography variant="caption" color="text.secondary">
          Back to certificates
        </Typography>
      </Stack>

      {/* Header card */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {cert.agent_name || cert.agent_id}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {cert.framework || '(unknown framework)'} · cert ID {cert.id?.slice(0, 8)}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip
                size="small"
                label={effectiveStatus}
                color={STATUS_COLOR[effectiveStatus] || 'default'}
              />
              {cert.latest_drift_verdict && (
                <Chip
                  size="small"
                  label={cert.latest_drift_verdict.replace('_', ' ').toLowerCase()}
                  sx={{
                    bgcolor: VERDICT_COLOR[cert.latest_drift_verdict] || '#90a4ae',
                    color: 'white',
                    fontWeight: 600,
                  }}
                />
              )}
              {cert.latest_improvement === 'POSITIVE_DRIFT' && (
                <Chip size="small" label="improved" sx={{ bgcolor: '#388e3c', color: 'white' }} />
              )}
            </Stack>
          </Stack>

          <Divider sx={{ my: 2 }} />

          <Stack direction="row" spacing={4} flexWrap="wrap">
            <Box>
              <Typography variant="caption" color="text.secondary">Issued</Typography>
              <Typography variant="body2">
                {cert.issued_at ? new Date(cert.issued_at).toLocaleDateString() : '—'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Expires</Typography>
              <Typography variant="body2">
                {cert.expires_at ? new Date(cert.expires_at).toLocaleDateString() : '—'}
                {effectiveStatus === 'expired' && (
                  <Typography component="span" variant="caption" color="error.main" sx={{ ml: 1 }}>
                    (expired)
                  </Typography>
                )}
              </Typography>
            </Box>
            {cert.suspended_at && (
              <Box>
                <Typography variant="caption" color="text.secondary">Suspended</Typography>
                <Typography variant="body2">
                  {new Date(cert.suspended_at).toLocaleDateString()}
                </Typography>
              </Box>
            )}
            <Box>
              <Typography variant="caption" color="text.secondary">30d events</Typography>
              <Typography variant="body2">
                {driftStats.drift_velocity_30d ?? 0} (
                {driftStats.critical_events_30d ?? 0} critical)
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Trend</Typography>
              <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                {driftStats.trend || 'stable'}
              </Typography>
            </Box>
          </Stack>

          {canApprove && (
            <>
              <Divider sx={{ my: 2 }} />
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2" sx={{ flex: 1 }}>
                  This certificate is suspended. Review the drift and approve to re-attest the new baseline.
                </Typography>
                <Button variant="contained" size="small" onClick={() => setApproveOpen(true)}>
                  Approve drift
                </Button>
              </Stack>
            </>
          )}

          {error && (
            <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Latest drift event detail */}
      {driftEvents[0] && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
              <Typography variant="overline" color="text.secondary">
                Latest drift event
              </Typography>
              <DriftFeedbackBar eventId={driftEvents[0].id} />
            </Stack>
            <Typography variant="body1" sx={{ mb: 1 }}>
              {driftEvents[0].summary || driftEvents[0].verdict}
            </Typography>
            <ComponentDriftTable componentDrifts={driftEvents[0].component_drifts} />
          </CardContent>
        </Card>
      )}

      {/* Timeline */}
      <Card>
        <CardContent>
          <Typography variant="overline" color="text.secondary">
            Drift + attestation timeline
          </Typography>
          {timeline.length === 0 ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              No events recorded yet.
            </Typography>
          ) : (
            <Stack spacing={0} sx={{ mt: 1, position: 'relative' }}>
              <Box
                sx={{
                  position: 'absolute',
                  top: 8,
                  bottom: 8,
                  left: 5,
                  width: 2,
                  bgcolor: 'divider',
                }}
              />
              {timeline.map((t, i) => (
                <TimelineRow key={i} entry={t} />
              ))}
            </Stack>
          )}
        </CardContent>
      </Card>

      {/* Approve dialog */}
      <Dialog open={approveOpen} onClose={() => setApproveOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Approve drift</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Approving rotates the baseline to the current fingerprint so future scans
            measure drift against this approved configuration. A reason is required and
            captured in the audit log.
          </Typography>
          <TextField
            label="Reason"
            multiline
            minRows={3}
            fullWidth
            autoFocus
            value={approveReason}
            onChange={(e) => setApproveReason(e.target.value)}
            placeholder="e.g., Reviewed the new tool addition with the agent team; intentional."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveOpen(false)} disabled={submitting}>Cancel</Button>
          <Button
            variant="contained"
            onClick={onApprove}
            disabled={!approveReason.trim() || submitting}
          >
            {submitting ? 'Approving…' : 'Approve'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

/**
 * P2c — compact feedback chips for a drift event.
 *
 * Mirrors the finding-feedback affordance: 5 verdict-quality options,
 * one click submits, the chosen one stays solid afterwards (and is
 * delete-able by re-clicking). Optional comment via a popover prompt.
 */
function DriftFeedbackBar({ eventId }: { eventId: string }) {
  const [current, setCurrent] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [hover, setHover] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    analyticsApi.getDriftFeedback(eventId)
      .then((r) => { if (!cancelled) setCurrent(r.data?.feedback?.feedback_type || null) })
      .catch(() => {})
    return () => { cancelled = true }
  }, [eventId])

  const options: Array<{ type: string; label: string; tone: 'success' | 'warning' | 'error' | 'default' }> = [
    { type: 'correct', label: 'Correct', tone: 'success' },
    { type: 'false_positive', label: 'False positive', tone: 'error' },
    { type: 'severity_too_high', label: 'Sev too high', tone: 'warning' },
    { type: 'severity_too_low', label: 'Sev too low', tone: 'warning' },
    { type: 'unclear', label: 'Unclear', tone: 'default' },
  ]

  const submit = async (type: string) => {
    if (busy) return
    setBusy(true)
    try {
      if (current === type) {
        await analyticsApi.deleteDriftFeedback(eventId)
        setCurrent(null)
      } else {
        await analyticsApi.submitDriftFeedback(eventId, { feedback_type: type })
        setCurrent(type)
      }
    } catch {
      // swallow — feedback is non-blocking; user can retry.
    } finally {
      setBusy(false)
    }
  }

  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
        Was this useful?
      </Typography>
      {options.map((o) => (
        <Chip
          key={o.type}
          size="small"
          label={o.label}
          variant={current === o.type ? 'filled' : 'outlined'}
          color={current === o.type ? o.tone : 'default'}
          onMouseEnter={() => setHover(o.type)}
          onMouseLeave={() => setHover(null)}
          onClick={() => submit(o.type)}
          sx={{
            height: 22,
            fontSize: 10,
            cursor: busy ? 'wait' : 'pointer',
            opacity: hover && hover !== o.type && current !== o.type ? 0.7 : 1,
          }}
        />
      ))}
    </Stack>
  )
}


function TimelineRow({ entry }: { entry: any }) {
  const color =
    entry.kind === 'approval' ? '#7b1fa2'
    : VERDICT_COLOR[entry.verdict] || '#90a4ae'
  const label = entry.kind === 'approval'
    ? 'Approved'
    : (entry.verdict || '').replace('_', ' ').toLowerCase()
  return (
    <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ py: 1.5, pl: 1.5 }}>
      <Box
        sx={{
          width: 12,
          height: 12,
          borderRadius: '50%',
          bgcolor: color,
          mt: 0.5,
          flexShrink: 0,
          position: 'relative',
          zIndex: 1,
          boxShadow: '0 0 0 2px white',
        }}
      />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap">
          <Typography variant="body2" sx={{ fontWeight: 600, textTransform: 'capitalize' }}>
            {label}
          </Typography>
          {entry.improvement === 'POSITIVE_DRIFT' && (
            <Chip size="small" label="improved" sx={{ height: 18, fontSize: 10, bgcolor: '#388e3c', color: 'white' }} />
          )}
          {entry.drift_score != null && (
            <Typography variant="caption" color="text.secondary">
              score {entry.drift_score.toFixed(2)}
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
            {entry.timestamp ? new Date(entry.timestamp).toLocaleString() : ''}
          </Typography>
        </Stack>
        {entry.kind === 'approval' && (
          <>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
              by {entry.approved_by_email || entry.approved_by_user_id}
              {entry.approved_verdict ? ` · approved ${entry.approved_verdict.replace('_', ' ').toLowerCase()}` : ''}
            </Typography>
            {entry.reason && (
              <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
                "{entry.reason}"
              </Typography>
            )}
          </>
        )}
      </Box>
    </Stack>
  )
}

function ComponentDriftTable({ componentDrifts }: { componentDrifts: any }) {
  if (!componentDrifts) return null
  const rows = Object.entries(componentDrifts).filter(
    ([k, v]: [string, any]) => !k.startsWith('_') && v && v.changed,
  )
  if (rows.length === 0) {
    return (
      <Typography variant="caption" color="text.secondary">
        No component-level changes recorded.
      </Typography>
    )
  }
  return (
    <Table size="small" sx={{ mt: 1 }}>
      <TableHead>
        <TableRow>
          <TableCell>Component</TableCell>
          <TableCell>Detail</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map(([name, drift]: [string, any]) => (
          <TableRow key={name}>
            <TableCell sx={{ textTransform: 'capitalize', fontWeight: 600, whiteSpace: 'nowrap' }}>
              {name.replace('_', ' ')}
              {drift.is_critical && (
                <Tooltip title="Critical change">
                  <Chip size="small" label="critical" sx={{ ml: 1, height: 18, fontSize: 10, bgcolor: '#d32f2f', color: 'white' }} />
                </Tooltip>
              )}
            </TableCell>
            <TableCell>
              <ComponentDetail drift={drift} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

function ComponentDetail({ drift }: { drift: any }) {
  // Friendly summary per component
  if (drift.previous_model && drift.current_model) {
    return (
      <Typography variant="caption">
        {drift.previous_model} → {drift.current_model}
        {drift.previous_family !== drift.current_family && ` (${drift.previous_family} → ${drift.current_family})`}
      </Typography>
    )
  }
  if (drift.added?.length || drift.removed?.length) {
    return (
      <Typography variant="caption">
        {drift.added?.length ? `+${drift.added.length} added` : ''}
        {drift.added?.length && drift.removed?.length ? ' · ' : ''}
        {drift.removed?.length ? `-${drift.removed.length} removed` : ''}
      </Typography>
    )
  }
  if (drift.delta != null) {
    return (
      <Typography variant="caption">
        {drift.previous_score} → {drift.current_score} ({drift.delta > 0 ? '+' : ''}{drift.delta})
      </Typography>
    )
  }
  if (drift.depth_increased || drift.hitl_removed) {
    return (
      <Typography variant="caption">
        {drift.depth_increased ? 'orchestration depth increased' : ''}
        {drift.depth_increased && drift.hitl_removed ? ' · ' : ''}
        {drift.hitl_removed ? 'human-in-loop removed' : ''}
      </Typography>
    )
  }
  return <Typography variant="caption" color="text.secondary">changed</Typography>
}
